import React from 'react';
import Select from 'components/shared/Select/Select';
import { Colorways } from 'types/constants';
import { DEFAULT_HIGHLIGHT_COLOR, isDarkColorway, MIN_COLS, COLS_TO_SKIP, INIT_COL_CLASS } from '../consts';
import styles from '../RichTextTable.module.css';
import { Node } from '@contentful/rich-text-types';
import { TableRow, SelectOption } from '../types';
import { getColorwayName } from 'utils/formatters';
import type { Color } from 'libs/contentful/types';
import { getColorway, getCustomBackgroundColor } from 'utils/styles';
import { TableWrapper } from './TableWrapper';

const COLUMN_SELECT_CLASS = 'column-select';

const getTbodyColorway = (colorway: { name: Colorways } | Colorways): string => {
  const color = getColorwayName(colorway);

  switch (color) {
    case Colorways.Secondary:
    case Colorways.Tertiary:
      return styles.tbodyWhiteColorway;
    case Colorways.Inverse:
      return styles.tbodyMediumGreenColorway;
    default:
    case Colorways.Primary:
      return styles.tbodyLightGreenColorway;
  }
};
const switchColumn = (e: React.MouseEvent<Element>) => {
  const selectedColumn = (e.target as HTMLElement).dataset.columns;
  const thisTable = (e.target as HTMLElement).closest(`.${COLUMN_SELECT_CLASS}`)?.nextElementSibling;
  thisTable?.querySelectorAll(`.${INIT_COL_CLASS}`).forEach((cell: Element) => {
    cell.classList.remove(INIT_COL_CLASS);
  });

  thisTable?.querySelectorAll(`[data-column]`).forEach((cell: Element) => {
    if (cell.getAttribute('data-column') === selectedColumn) {
      cell.classList.remove(styles.colHidden);
    } else if (parseInt(cell.getAttribute('data-column') ?? '0') > COLS_TO_SKIP - 1) {
      cell.classList.add(styles.colHidden);
    }
  });
};

export const renderTable = (
  _node: Node,
  children: React.ReactNode,
  color: Colorways,
  highlightedColumnIndex: number,
  highlightColor: Color | undefined,
) => {
  const rows = React.Children.toArray(children) as TableRow[];
  const colCount = rows.filter(c => c.type === 'tr')[0].props.children[0].length;
  const moreThanMin = colCount > MIN_COLS;

  const selectOptions: SelectOption[] | null = moreThanMin
    ? rows[0].props.children[0].slice(COLS_TO_SKIP).map(option => ({
        key: (typeof option.props.children[0] === 'string'
          ? option.props.children[0]
          : option?.props?.children[0]?.props?.children)!,
      }))
    : null;

  const customBg = highlightColor ? getCustomBackgroundColor(highlightColor) : undefined;
  const highlightColorwayClass = getColorway(highlightColor ?? DEFAULT_HIGHLIGHT_COLOR);
  const highlightContent =
    customBg || highlightColor?.name === Colorways.Custom || isDarkColorway(highlightColorwayClass)
      ? 'inverse'
      : 'primary';

  return (
    <TableWrapper>
      {moreThanMin && (
        <div className={`${COLUMN_SELECT_CLASS} font-sans font-semibold mb-8 sm:hidden`}>
          Chime vs&nbsp;
          <Select
            optionHeader="Compare to"
            selectOptions={selectOptions ?? []}
            onOptionClick={switchColumn}
            colsToSkip={COLS_TO_SKIP}
          />
        </div>
      )}
      <table
        className={styles.richTextTable}
        data-highlighted-column-index={highlightedColumnIndex}
        data-highlight-content={highlightContent}>
        <colgroup>
          {Array.from({ length: colCount }, (_, i) => {
            const isHighlighted = i === highlightedColumnIndex;
            return (
              <col
                key={i}
                {...(isHighlighted && (customBg ? { style: customBg } : { className: highlightColorwayClass }))}
              />
            );
          })}
        </colgroup>
        <tbody className={getTbodyColorway(color)}>{children}</tbody>
      </table>
    </TableWrapper>
  );
};
